import clsx from "clsx";
import { ImgHTMLAttributes } from "react";

const PRESET_SIZES = { navbar: 40, app: 60 } as const;
export type LogoSizePreset = keyof typeof PRESET_SIZES;

export interface LogoProps extends ImgHTMLAttributes<HTMLImageElement> {
  imgSrc: string;
  size?: number | LogoSizePreset;
  alt: string;
  // Neue Option für einheitliche Größe
  uniform?: boolean;
}

/**
 * LogoImage – rendert ein responsives Logo mit optionaler Größensteuerung.
 *
 * @param uniform - wenn true, werden alle Logos in einen quadratischen Container gefittet
 */
export const LogoImage = ({
  imgSrc,
  size,
  alt,
  className,
  uniform = false,
  ...rest
}: LogoProps) => {
  const px = size === undefined ? undefined : typeof size === "number" ? size : PRESET_SIZES[size];

  if (uniform && px) {
    // Option 1: Quadratischer Container für einheitliche Größe
    return (
      <div
        className={clsx("flex items-center justify-center shrink-0", className)}
        style={{ width: px, height: px }}
      >
        <img
          src={imgSrc}
          alt={alt}
          loading="lazy"
          className="max-w-full max-h-full object-contain"
          {...rest}
        />
      </div>
    );
  }

  // Option 2: Sowohl maxHeight als auch maxWidth setzen
  return (
    <img
      src={imgSrc}
      alt={alt}
      loading="lazy"
      className={clsx(
        "object-contain",
        "shrink-0",
        px && "h-auto w-auto", // beide Dimensionen flexibel
        className,
      )}
      style={
        px
          ? {
              maxHeight: px,
              maxWidth: px, // Breite ebenfalls begrenzen
            }
          : undefined
      }
      {...rest}
    />
  );
};

// Alternative: Spezifische Varianten für verschiedene Anwendungsfälle
export const NavbarLogo = (props: Omit<LogoProps, "size">) => (
  <LogoImage {...props} size="navbar" uniform />
);

export const AppLogo = (props: Omit<LogoProps, "size">) => (
  <LogoImage {...props} size="app" uniform={false} />
);
